import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  CheckCircle2, AlertTriangle, Loader2, PlugZap, Power, AlertCircle, Calendar,
  Video, Facebook, Instagram, Music2, Radio,
} from "lucide-react";
import TodaysPlan from "@/components/TodaysPlan";
import { StreakBadge, ReachWidget } from "@/components/Widgets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cn = (...c) => c.filter(Boolean).join(" ");

const PLATFORMS = [
  {
    key: "restream",
    name: "Restream",
    blurb: "Source of clips",
    Icon: Radio,
    accent: "text-cyan-300",
    secondary: (s) => s.restream_user_id && `User #${s.restream_user_id}`,
  },
  {
    key: "youtube",
    name: "YouTube",
    blurb: "Posts as Shorts",
    Icon: Video,
    accent: "text-red-400",
    secondary: (s) => s.channel_title || s.channel_handle,
  },
  {
    key: "facebook",
    name: "Facebook Page",
    blurb: "Posts as Reels",
    Icon: Facebook,
    accent: "text-blue-400",
    secondary: (s) => s.page_name,
  },
  {
    key: "instagram",
    name: "Instagram",
    blurb: "Business · Reels",
    Icon: Instagram,
    accent: "text-pink-400",
    secondary: (s) => s.ig_username && `@${s.ig_username}`,
  },
  {
    key: "tiktok",
    name: "TikTok",
    blurb: "Auto-post (after audit)",
    Icon: Music2,
    accent: "text-fuchsia-300",
    secondary: (s) => s.display_name && `@${s.display_name}`,
  },
];

function Banner({ kind, msg, onClose }) {
  return (
    <div
      data-testid="callback-banner"
      className={cn(
        "mb-6 flex items-start justify-between gap-4 rounded-md px-4 py-3 text-sm ring-1",
        kind === "ok"
          ? "bg-emerald-500/10 text-emerald-200 ring-emerald-500/30"
          : "bg-red-500/10 text-red-200 ring-red-500/30",
      )}
    >
      <span>{msg}</span>
      <button onClick={onClose} className="text-xs underline opacity-70 hover:opacity-100">dismiss</button>
    </div>
  );
}

function IntegrationCard({ platform, status }) {
  const { key, name, blurb, Icon, accent, secondary } = platform;
  const [busy, setBusy] = useState(false);
  const connected = status?.connected;
  const needsReauth = !!status?.needs_reauth;
  const sub = connected && secondary ? secondary(status) : null;
  const unaudited = key === "tiktok" && status?.unaudited;

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/auth/${key}/login`);
      window.location.href = r.data.url;
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Failed to start OAuth.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/${key}/disconnect`);
      window.location.reload();
    } catch (e) {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-lg border p-5",
        needsReauth
          ? "border-amber-500/40 bg-amber-500/5 ring-1 ring-amber-500/20"
          : "border-zinc-800 bg-zinc-900/40",
      )}
      data-testid={`integration-card-${key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-950 ring-1 ring-zinc-800", accent)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-medium">{name}</p>
            <p className="text-xs text-zinc-500">{blurb}</p>
          </div>
        </div>
        {needsReauth ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium text-amber-300 ring-1 ring-amber-500/40"
            data-testid={`reauth-badge-${key}`}
          >
            <AlertTriangle className="h-3.5 w-3.5" /> Reconnect required
          </span>
        ) : connected ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/30">
            <CheckCircle2 className="h-3.5 w-3.5" /> Connected
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-2.5 py-1 text-xs font-medium text-zinc-400 ring-1 ring-zinc-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Not connected
          </span>
        )}
      </div>

      {sub && <p className="-mt-2 text-xs text-zinc-400">{sub}</p>}

      {needsReauth && status?.needs_reauth_reason && (
        <p className="-mt-2 text-[11px] text-amber-200/80">
          {String(status.needs_reauth_reason).slice(0, 220)}
        </p>
      )}

      {unaudited && (
        <div className="rounded-md bg-amber-400/10 px-3 py-2 text-[11px] text-amber-200 ring-1 ring-amber-400/30">
          ⚠️ Posts will go to your TikTok drafts inbox until your app is audited for direct publishing. Tap "Publish" in the TikTok app to make them live.
          <button
            data-testid="tiktok-audit-complete-btn"
            onClick={async () => {
              if (!window.confirm("Only flip this AFTER TikTok has approved your app for Production posting. Direct publishing will start on the NEXT scheduled post. You will also need to click Reconnect afterwards so the new video.publish scope is granted.\n\nProceed?")) return;
              try {
                await axios.put(`${API}/integrations/tiktok`, { posting_mode: "direct" });
                window.alert("Switched TikTok to direct mode. Please click Disconnect → Connect again to grant the video.publish scope.");
                window.location.reload();
              } catch (e) {
                window.alert("Failed: " + (e?.response?.data?.detail || e.message));
              }
            }}
            className="mt-2 inline-flex items-center gap-1 rounded bg-amber-400/20 px-2 py-1 text-[10px] font-medium text-amber-100 ring-1 ring-amber-400/40 hover:bg-amber-400/30"
          >
            Audit complete → enable direct posting
          </button>
        </div>
      )}

      <div className="mt-auto flex gap-2">
        {needsReauth ? (
          <button
            onClick={connect}
            disabled={busy}
            data-testid={`reconnect-${key}-btn`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-500/15 px-3 py-2 text-sm font-medium text-amber-200 ring-1 ring-amber-500/40 hover:bg-amber-500/25 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
            Reconnect
          </button>
        ) : connected ? (
          <button
            onClick={disconnect}
            disabled={busy}
            data-testid={`disconnect-${key}-btn`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-red-500/10 px-3 py-2 text-sm font-medium text-red-300 ring-1 ring-red-500/30 hover:bg-red-500/20 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
            Disconnect
          </button>
        ) : (
          <button
            onClick={connect}
            disabled={busy}
            data-testid={`connect-${key}-btn`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-200 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
            Connect
          </button>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected")) {
      setBanner({ kind: "ok", msg: `${p.get("connected").replace(/^./, c => c.toUpperCase())} connected successfully.` });
      window.history.replaceState({}, document.title, "/");
    } else if (p.get("error")) {
      const err = p.get("error");
      const plat = p.get("platform");
      let msg;
      if (err === "no_pages") {
        msg = `${plat === "instagram" ? "Instagram" : "Facebook"} connected, but Meta returned 0 Pages. ` +
          `Click Connect again and make sure your church Facebook Page is ticked on Meta's "Choose Pages" screen. ` +
          `If you don't see that screen, you may need to add yourself as a Tester at developers.facebook.com → App Roles.`;
      } else {
        msg = `OAuth error: ${err}${plat ? ` (${plat})` : ""}`;
      }
      setBanner({ kind: "bad", msg });
      window.history.replaceState({}, document.title, "/");
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/integrations/summary`);
      setSummary(r.data);
    } catch (e) {
      setBanner({ kind: "bad", msg: "Failed to load integration status." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const connectedCount = summary ? Object.values(summary).filter(v => v.connected).length : 0;

  return (
    <div data-testid="dashboard-page">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs uppercase tracking-[0.2em] text-zinc-500">Dashboard</p>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Connect your <span className="text-amber-400">platforms</span>
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-400">
            {loading ? "Loading integration status…" : `${connectedCount} of 5 platforms connected. Once all 5 are live, the daily scheduler can auto-post 2 clips a day.`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StreakBadge />
          <div className="w-64"><ReachWidget /></div>
        </div>
      </header>

      {banner && <Banner {...banner} onClose={() => setBanner(null)} />}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="integrations-grid">
        {PLATFORMS.map((p) => (
          <IntegrationCard key={p.key} platform={p} status={summary?.[p.key] || { connected: false }} />
        ))}
        {/* Scheduler placeholder replaced with live Today's Plan */}
        <div className="md:col-span-2 xl:col-span-3">
          <TodaysPlan />
        </div>
      </section>
    </div>
  );
}
